import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { OrderUtilityService } from './../../../../services';
import { OrderControllerService } from '../../../../services/order/order-controller.service';
import { LineStatus } from './../../../../models/enums';
import { OrderShipmentGroup, OrderShipment, OrderFilter } from './../../../../models';
import { takeUntil } from 'rxjs/operators';
import { LegendItem } from '../../models/legend-item';
import { OrderShipmentCount } from '../../models/order-shipment-count';
import { RouterLink } from '@angular/router';
import { FiltersComponent } from '../../../../shared/components/filters/filters.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { LegendItemComponent } from '../legend-item/legend-item.component';
import { BarChartModule } from '@swimlane/ngx-charts';
import { OrderShipmentGroupsList } from '../../../../models/order-shipment-groups-list';

@Component({
  selector: 'insight-errors-by-component',
  templateUrl: './errors-by-component.component.html',
  styleUrls: ['./errors-by-component.component.scss', '../../scss/operational-dashboard-shared.scss'],
  imports: [RouterLink, FiltersComponent, LoadingSpinnerComponent, LegendItemComponent, BarChartModule]
})
export class ErrorsByComponentComponent implements OnInit, OnDestroy {
  public didError = false;
  public isLoading = true;
  public filtersSubject: BehaviorSubject<OrderFilter> = new BehaviorSubject<OrderFilter>(new OrderFilter());
  public allOrderShipmentGroupsCopy: OrderShipmentGroupsList;
  public filteredChartData = [];

  // labels
  public xAxisLabel: string = 'Status';
  public yAxisLabel: string = 'Orders';

  // legend items to show/hide by checkboxes
  public legendItems: LegendItem[] = [];

  // chart colors
  public chartColors = { name: '', selectable: true, group: 'quantile', domain: [] };

  private searchText = '';
  private $unsubscribeSubject = new Subject();

  constructor(
    private orderControllerService: OrderControllerService,
    private orderUtilityService: OrderUtilityService) { }

  ngOnInit() {
    this.legendItems = this.orderUtilityService.getOrderLegendItems();
    this.chartColors.domain = this.legendItems.map(legendItem => legendItem.color);

    this.orderControllerService.orderDataRefreshedEvent
      .pipe(takeUntil(this.$unsubscribeSubject))
      .subscribe(() => {
        if (this.isLoading || this.orderControllerService.orderDataNeedsRefresh()) {
          this.allOrderShipmentGroupsCopy = this.orderControllerService.getAllOrderShipments();
          this.applyFilters();
          this.isLoading = false;
        }
      });

    // watch for filter changes and update chart
    this.filtersSubject
      .pipe(takeUntil(this.$unsubscribeSubject))
      .subscribe(() => {
        this.applyFilters();
      });
  }

  ngOnDestroy() {
    this.$unsubscribeSubject.next();
    this.$unsubscribeSubject.complete();
  }

  public applyFilters(): void {
    const filteredData = this.orderUtilityService.applyFilters(this.allOrderShipmentGroupsCopy, this.filtersSubject.value, this.searchText);
    this.populateChartData(filteredData);
  }

  private populateChartData(groups: OrderShipmentGroup[]): void {
    // all order shipments for groups that aren't complete
    const allOrderShipments: OrderShipment[] = groups
      .filter(g => g.status !== LineStatus.Complete)
      .map(g => g.orderShipments)
      .flat();

    // get all statuses
    const statuses: number[] = Object.keys(LineStatus).map(key => LineStatus[key]);

    // calculate the number of order shipments by component for each status
    const newChartData = [];
    for (const status of statuses) {
      const orders = allOrderShipments.filter(os => os.status == status);
      const orderCounts = new OrderShipmentCount(orders);

      if (orders.length > 0) {
        newChartData.push({
          'name': this.orderUtilityService.mapOrderShipmentStatus(status),
          'series': [
            { 'name': 'ADEBS - CVD', 'value': orderCounts.adebsCVD },
            { 'name': 'ADEBS - HDD', 'value': orderCounts.adebsHardDrive },
            { 'name': 'ADEBS - Electronic', 'value': orderCounts.adebsElectronic },
            { 'name': 'Streaming - Electronic', 'value': orderCounts.streamingElectronic },
            { 'name': 'Streaming - HDD', 'value': orderCounts.streamingHardDrive },
            { 'name': 'Cinelink - Electronic', 'value': orderCounts.cinelinkElectronic },
            { 'name': 'Cinelink - HDD', 'value': orderCounts.cinelinkHardDrive }
          ]
        });
      }
    }

    this.filteredChartData = newChartData;
  }
}
